import type { ReactNode } from 'react';

import Slider from '@cartwhl/ui/atoms/Slider';
import Text from '@cartwhl/ui/base/TextNew';
import Overlay from '@cartwhl/ui/player/Overlay';

import {
  REPATHING_BRUSH_RADIUS_MAX,
  REPATHING_BRUSH_RADIUS_MIN,
  REPATHING_BRUSH_STRENGTH_MAX,
  REPATHING_BRUSH_STRENGTH_MIN,
} from './constants';
import { useStore } from './repathingStore';

export default function RepathingControlsOptions() {
  const store = useStore();
  const { brushRadius, brushStrength, curve, generation, headingMode, tool } =
    store.state;
  const disabled = !curve || generation.status === 'pending';

  return (
    <>
      <Overlay.Group orientation="vertical">
        <Text token="12/medium" className="text-primary mb-1">
          Tool:
        </Text>
        <Overlay.Group orientation="horizontal">
          <Overlay.Button
            active={tool === 'curve'}
            disabled={generation.status === 'pending'}
            text="Curve"
            tooltipText="Edit curve controls"
            onClick={() => store.setTool('curve')}
          />
          <Overlay.Button
            active={tool === 'nudge'}
            disabled={generation.status === 'pending'}
            text="Nudge"
            tooltipText="Brush the path"
            onClick={() => store.setTool('nudge')}
          />
        </Overlay.Group>
      </Overlay.Group>

      {tool === 'nudge' ? (
        <Overlay.Group orientation="vertical" className="gap-2">
          <ControlSlider
            label="Radius"
            value={brushRadius}
            min={REPATHING_BRUSH_RADIUS_MIN}
            max={REPATHING_BRUSH_RADIUS_MAX}
            step={0.05}
            disabled={disabled}
            onChange={(value) => store.setBrushRadius(value)}
          />
          <ControlSlider
            label="Strength"
            value={brushStrength}
            min={REPATHING_BRUSH_STRENGTH_MIN}
            max={REPATHING_BRUSH_STRENGTH_MAX}
            step={0.05}
            disabled={disabled}
            onChange={(value) => store.setBrushStrength(value)}
          />
        </Overlay.Group>
      ) : null}

      <Overlay.Divider />

      <Overlay.Group orientation="vertical">
        <Text token="12/medium" className="text-primary mb-1">
          Path cleanup:
        </Text>
        <Overlay.Group orientation="horizontal">
          <Overlay.Button
            disabled={disabled}
            text="Smoothen"
            tooltipText="Smooth the path"
            onClick={() => store.smoothCurve()}
          />
          <Overlay.Button
            disabled={disabled}
            text="Simplify"
            tooltipText="Reduce path controls"
            onClick={() => store.simplifyCurve()}
          />
        </Overlay.Group>
      </Overlay.Group>

      <Overlay.Group orientation="vertical">
        <Text token="12/medium" className="text-primary mb-1">
          Heading:
        </Text>
        <Overlay.Group orientation="horizontal">
          <Overlay.Button
            active={headingMode === 'original'}
            disabled={disabled}
            text="Original"
            tooltipText="Keep original facing"
            onClick={() => store.setHeadingMode('original')}
          />
          <Overlay.Button
            active={headingMode === 'toPath'}
            disabled={disabled}
            text="To path"
            tooltipText="Face along the path"
            onClick={() => store.setHeadingMode('toPath')}
          />
        </Overlay.Group>
      </Overlay.Group>

      {!curve ? (
        <RepathingStatus tone="warning">
          <Text token="12" className="text-pretty text-inherit">
            Select a loaded motion to edit its path.
          </Text>
        </RepathingStatus>
      ) : generation.status === 'pending' ? (
        <RepathingStatus tone="active">
          <Text token="12" className="text-pretty text-inherit">
            Regenerating path…
          </Text>
        </RepathingStatus>
      ) : generation.status === 'failed' && generation.error ? (
        <RepathingStatus tone="warning">
          <Text token="12" className="text-pretty text-inherit">
            {generation.error}
          </Text>
        </RepathingStatus>
      ) : null}
    </>
  );
}

function RepathingStatus({
  children,
  tone,
}: {
  children: ReactNode;
  tone: 'active' | 'warning';
}) {
  return (
    <div
      className={`rounded-lg px-2 py-1 ${
        tone === 'active'
          ? 'bg-blue/15 text-primary'
          : 'text-primary bg-yellow-50/25'
      }`}
    >
      {children}
    </div>
  );
}

interface ControlSliderProps {
  disabled: boolean;
  label: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
  step: number;
  value: number;
}

function ControlSlider({
  disabled,
  label,
  max,
  min,
  onChange,
  step,
  value,
}: ControlSliderProps) {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center justify-between gap-2">
        <Text token="12/medium" className="text-primary">
          {label}
        </Text>
        <Text token="12" className="text-secondary tabular-nums">
          {value.toFixed(2)}
        </Text>
      </span>
      <Slider
        currentValue={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        size="sm"
        trackColor="player"
        rangeColor="blue"
        onChange={onChange}
      />
    </label>
  );
}
